import React, { useCallback, useMemo } from 'react';
import { AppButton, Box, FiservCardStatusEnum, Inscription, Preloader, RoundPendingIcon } from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/wrappers';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { useGetCardsQuery } from '@/modules/transaction/Cards/queries/useGetCards.query';

import { Dependent, DependentStatusEnum } from '../../employee.types';

import useActivateDependentQuery from './useActivateDependent.query';
import useRemoveDependentQuery from './useRemoveDependent.query';

interface Props {
  dependent: Dependent;
  setActivationDependentId: () => void;
}

const ActivationDependentModal: React.FC<Props> = ({ dependent, setActivationDependentId }) => {
  const isDeactivation = useMemo(() => dependent?.status === DependentStatusEnum.ACTIVE, [dependent]);
  const { response } = useGetCardsQuery(dependent?.employeeId, dependent?.dependentId);
  const hasActiveCard = useMemo(() => Boolean(response?.grouped_cards?.[FiservCardStatusEnum.ACTIVE]?.length),
    [response]);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { removeDependent, isLoading } = useRemoveDependentQuery();
  const { activateDependent, isLoading: isActivatingLoading } = useActivateDependentQuery();

  const onSuccess = useCallback(() => {
    const message = isDeactivation
      ? `${dependent?.firstName} ${dependent?.lastName} was successfully deactivated`
      : 'Dependent activated successfully!';
    setActivationDependentId();
    handleAddPermanentSnackbar({
      text: message,
      state: SnackbarState.positive,
      closeIcon: true,
    });
  }, [dependent, isDeactivation, setActivationDependentId, handleAddPermanentSnackbar]);

  const handleSubmit = useCallback(async() => {
    const dependentId = dependent?.dependentId;
    if (dependentId && isDeactivation) {
      await removeDependent(dependentId);
      onSuccess();
    }
    if (dependentId && !isDeactivation) {
      await activateDependent(dependentId);
      onSuccess();
    }
  }, [dependent, activateDependent, onSuccess, removeDependent, isDeactivation]);

  return (
    <ModalWrapper visible onSetVisible={setActivationDependentId}>
      <Box align="center" pad={{ vertical: 'spacing24' }}>
        <RoundPendingIcon color='warning' />
        <Inscription
          margin={{ top: 'spacing12' }}
          size='2xl'
          weight={700}
        >
          {isDeactivation
            ? 'Deactivate dependent?'
            : 'Activate dependent?'}
        </Inscription>
        {isDeactivation && hasActiveCard ? (
          <Box align="center" margin={{ top: 'spacing12' }}>
            <Inscription color="textBody" lineHeight="20px">
              A card has been found for {dependent?.fullName}.
            </Inscription>
            <Inscription color="textBody" lineHeight="20px">
              By deleting this dependent, their card will also be closed.
            </Inscription>
          </Box>
        ) : null}
        <Box margin={{ top: 'spacing24' }} direction="row">
          <Box margin={{ right: 'spacing12' }}>
            <AppButton
              onClick={setActivationDependentId}
              buttonType='secondary'
              width='140px'
              testId='cancel'
            >
              Cancel
            </AppButton>
          </Box>
          <Box>
            <AppButton
              onClick={handleSubmit}
              width='140px'
              testId={isDeactivation ? 'deactivation' : 'activation'}
              {...isDeactivation ? { color: 'red' } : {}}
            >
              {isLoading || isActivatingLoading
                ? <Preloader color='error' />
                : (isDeactivation ? 'Deactivate' : 'Activate')}
            </AppButton>
          </Box>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default ActivationDependentModal;
